// client/src/components/Reservations/services/reservationService.js
import axiosInstance from '../../../api/axiosInstance';

export const reservationService = {
  /**
   * Get user's reservations with filtering and pagination
   * @param {string} userId - User ID
   * @param {Object} params - Query parameters
   */
  async getUserReservations(userId, params = {}) {
    try {
  const response = await axiosInstance.get(`/users/${userId}/reservations`, { params });
      // Server returns: { reservations, pagination: { page, limit, total, hasMore } }
      const reservations = response.data.reservations || [];
      const pagination = response.data.pagination || {};
      return {
        reservations,
        hasMore: pagination.hasMore || false,
        total: pagination.total || 0,
        page: pagination.page || 1,
        limit: pagination.limit || 10
      };
    } catch (error) {
      console.error('Error fetching user reservations:', error);
      throw error;
    }
  },

  /**
   * Archive a reservation item (remove from user's list)
   * @param {string} itemId
   * @param {{ reason?: string }} data
   */
  async archiveReservationItem(itemId, data = {}) {
    try {
      const response = await axiosInstance.post(`/reservations/items/${itemId}/archive`, data);
      return response.data;
    } catch (error) {
      console.error('Error archiving reservation item:', error);
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Only cancelled or past items can be removed.');
      }
      if (error.response?.status === 403) {
        throw new Error('You cannot archive this reservation item');
      }
      if (error.response?.status === 404) {
        throw new Error('Reservation item not found');
      }
      throw new Error('Failed to archive reservation item. Please try again.');
    }
  },

  /**
   * Get a specific reservation by ID
   * @param {string} reservationId - Reservation ID
   */
  async getReservation(reservationId) {
    try {
  const response = await axiosInstance.get(`/reservations/${reservationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching reservation:', error);
      throw error;
    }
  },

  /**
   * Update a reservation
   * @param {string} reservationId - Reservation ID
   * @param {Object} updates - Fields to update
   */
  async updateReservation(reservationId, updates) {
    try {
  const response = await axiosInstance.patch(`/reservations/${reservationId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating reservation:', error);
      
      // Handle specific error cases
      if (error.response?.status === 409) {
        throw new Error(error.response.data.message || 'Time slot is no longer available');
      }
      if (error.response?.status === 400) {
        throw new Error(error.response.data.message || 'Invalid reservation update');
      }
      if (error.response?.status === 403) {
        throw new Error('You cannot modify this reservation');
      }
      
      throw new Error('Failed to update reservation. Please try again.');
    }
  },

  /**
   * Cancel a reservation
   * @param {string} reservationId - Reservation ID
   * @param {Object} data - Cancellation data (reason, etc.)
   */
  async cancelReservation(reservationId, data) {
    console.warn('[reservationService] cancelReservation(reservationId) is deprecated. Use cancelReservationItem(itemId, data).');
    const response = await axiosInstance.post(`/reservations/${reservationId}/cancel`, data);
    return response.data;
  },

  /**
   * Cancel a reservation ITEM (preferred)
   * @param {string} itemId
   * @param {{reason?: string}} data
   */
  async cancelReservationItem(itemId, data) {
    try {
      const response = await axiosInstance.post(`/reservations/items/${itemId}/cancel`, data);
      return response.data;
    } catch (error) {
      console.error('Error cancelling reservation item:', error);
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.error || 'Cannot cancel this reservation item');
      }
      if (error.response?.status === 403) {
        throw new Error('You cannot cancel this reservation item');
      }
      if (error.response?.status === 404) {
        throw new Error('Reservation item not found');
      }
      if (error.response?.status === 409) {
        throw new Error('Reservation item already cancelled');
      }
      throw new Error('Failed to cancel reservation item. Please try again.');
    }
  },

  /**
   * Get reservation cancellation policies
   * @param {string} businessId - Business ID
   */
  async getCancellationPolicies(businessId) {
    try {
  const response = await axiosInstance.get(`/businesses/${businessId}/cancellation-policies`);
      return response.data;
    } catch (error) {
      console.error('Error fetching cancellation policies:', error);
      return {
        min_hours_notice: 24,
        cancellation_fee: 0,
        policy_text: 'Please cancel at least 24 hours in advance to avoid charges.'
      };
    }
  },

  /**
   * Check if reservation can be modified
   * @param {Object} reservation - Reservation object
   */
  canModifyReservation(reservation) {
    if (!reservation) return false;
    
    // Cannot modify cancelled reservations
    if (reservation.status === 'cancelled') return false;
    
    // Cannot modify past reservations
    const reservationDateTime = new Date(`${reservation.date}T${reservation.time}`);
    const now = new Date();
    if (reservationDateTime <= now) return false;
    
    // Check minimum modification notice (default 2 hours)
    const minModificationHours = reservation.min_modification_hours || 2;
    const minModificationTime = new Date(now.getTime() + (minModificationHours * 60 * 60 * 1000));
    if (reservationDateTime <= minModificationTime) return false;
    
    return true;
  },

  /**
   * Check if reservation can be cancelled
   * @param {Object} reservation - Reservation object
   */
  canCancelReservation(reservation) {
    if (!reservation) return false;
    
    // Cannot cancel already cancelled reservations
    if (reservation.status === 'cancelled') return false;
    
    // Cannot cancel past reservations
    const reservationDateTime = new Date(`${reservation.date}T${reservation.time}`);
    const now = new Date();
    if (reservationDateTime <= now) return false;
    
    return true;
  },

  /**
   * Get modification restrictions for a reservation
   * @param {Object} reservation - Reservation object
   */
  getModificationRestrictions(reservation) {
    const restrictions = {
      canModifyDate: true,
      canModifyTime: true,
      canModifyPartySize: true,
      canAddServices: false, // Typically not allowed in modifications
      minNoticeHours: reservation.min_modification_hours || 2
    };

    if (!this.canModifyReservation(reservation)) {
      return {
        ...restrictions,
        canModifyDate: false,
        canModifyTime: false,
        canModifyPartySize: false,
        canAddServices: false
      };
    }

    // Check time restrictions
    const reservationDateTime = new Date(`${reservation.date}T${reservation.time}`);
    const now = new Date();
    const hoursUntilReservation = (reservationDateTime - now) / (1000 * 60 * 60);

    // If less than 24 hours, restrict date changes
    if (hoursUntilReservation < 24) {
      restrictions.canModifyDate = false;
    }

    // If less than 4 hours, restrict time changes
    if (hoursUntilReservation < 4) {
      restrictions.canModifyTime = false;
    }

    return restrictions;
  }
};

export default reservationService;